import { WorkingHour } from "@/app/types/common";

const DAYS_OF_WEEK = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

const TIME_ZONE_ALIASES: Record<string, string> = {
  EST: "America/New_York",
  EDT: "America/New_York",
  CST: "America/Chicago",
  CDT: "America/Chicago",
  MST: "America/Denver",
  MDT: "America/Denver",
  PST: "America/Los_Angeles",
  PDT: "America/Los_Angeles",
};

/**
 * Parses a time string like "8:00am" or "5:00pm" into minutes since midnight
 */
const parseTimeToMinutes = (timeStr: string): number => {
  const match = /^(\d{1,2}):(\d{2})(am|pm)$/.exec(timeStr.toLowerCase());
  if (!match) return 0;

  let hours = Number.parseInt(match[1], 10);
  const minutes = Number.parseInt(match[2], 10);
  const period = match[3];

  if (period === "pm" && hours !== 12) {
    hours += 12;
  } else if (period === "am" && hours === 12) {
    hours = 0;
  }

  return hours * 60 + minutes;
};

/**
 * Gets the current time as minutes since midnight
 */
const getCurrentTimeInMinutes = (now: Date): number => {
  return now.getHours() * 60 + now.getMinutes();
};

const getOfficeTime = (now: Date, timeZone?: string): Date => {
  if (!timeZone) {
    return now;
  }

  const resolvedTimeZone = TIME_ZONE_ALIASES[timeZone] ?? timeZone;

  try {
    const browserDate = new Date(now.toLocaleString("en-US"));
    const officeDate = new Date(now.toLocaleString("en-US", { timeZone: resolvedTimeZone }));
    const timezoneOffset = officeDate.getTime() - browserDate.getTime();

    return new Date(now.getTime() + timezoneOffset);
  } catch {
    return now;
  }
};

/**
 * Formats a time string to lowercase (e.g., "8:00AM" → "8:00am")
 */
const formatTime = (timeStr: string): string => {
  return timeStr.toLowerCase();
};

/**
 * Finds the next open day after the given day index
 */
const findNextOpenDay = (
  workingHours: WorkingHour[],
  todayIndex: number,
): WorkingHour | undefined => {
  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = (todayIndex + i) % 7;
    const nextDayName = DAYS_OF_WEEK[nextDayIndex];
    const nextDayHours = workingHours.find(
      (d) => d?.date?.toLowerCase() === nextDayName.toLowerCase() && d.open,
    );
    if (nextDayHours) {
      return nextDayHours;
    }
  }
  return undefined;
};

/**
 * Formats the closed status message with next open day info
 */
const getClosedStatus = (nextOpenDay: WorkingHour | undefined): string => {
  if (nextOpenDay?.open) {
    return `Closed • Opens ${nextOpenDay.date.slice(0, 3)} at ${formatTime(nextOpenDay.open.from)}`;
  }
  return "Closed";
};

/**
 * `now` is injectable so a caller can pin the clock (the scheduler's
 * phone-first arm specs); production callers omit it and read the real time.
 */
export function getOfficeStatus(
  workingHours: WorkingHour[],
  timeZone?: string,
  nowInput: Date = new Date(),
): string {
  if (!workingHours || workingHours.length === 0) {
    return "No working hours available";
  }

  const now = getOfficeTime(nowInput, timeZone);
  const todayIndex = now.getDay();
  const todayName = DAYS_OF_WEEK[todayIndex];

  const todayHours = workingHours.find((d) => d?.date?.toLowerCase() === todayName.toLowerCase());

  const nextOpenDay = findNextOpenDay(workingHours, todayIndex);

  // Closed all day - no hours for today
  if (!todayHours?.open) {
    return getClosedStatus(nextOpenDay);
  }

  const currentMinutes = getCurrentTimeInMinutes(now);
  const openMinutes = parseTimeToMinutes(todayHours.open.from);
  const closeMinutes = parseTimeToMinutes(todayHours.open.to);

  // Currently open
  if (currentMinutes >= openMinutes && currentMinutes < closeMinutes) {
    return `Open • Closes at ${formatTime(todayHours.open.to)}`;
  }

  // Before opening time
  if (currentMinutes < openMinutes) {
    return `Closed • Opens at ${formatTime(todayHours.open.from)}`;
  }

  // After closing time
  return getClosedStatus(nextOpenDay);
}
